/* eslint-disable no-nested-ternary */
import { Fragment, useEffect, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import PendingOrdersTable from '../components/PendingOrdersTable';
import {
  RootState,
  useGetEventOrderItemsQuery,
  useGetShowtimesQuery,
} from '../store';
import OrderItem from '../types/OrderItem';
import config from '../config/config';
import OrdersTableSkeleton from '../components/OrdersTableSkeleton';
import ApprovedOrdersTable from '../components/ApprovedOrdersTable';
import useGetParams from '../hooks/useGetParams';
import useNavigateWithQuery from '../hooks/useNavigateWithQuery';
import useGetUserRole from '../hooks/useGetUserRole';
import useGetSelectedEvent from '../hooks/useGetSelectedEvent';
import AdminSectionTitle from '../components/AdminSectionTitle';
import HorizontalTabs from '../components/atoms/HorizontalTabs';
import PendingPaymentsTable from '../components/PendingPaymentsTable';
import ShowtimeTabs from '../components/ShowtimeTabs';
import CoinsHand from '../components/icons/CoinsHand';
import isAllowed from '../utils/isAllowed';
import { UserRole } from '../models/user';
import BatchManualOrdersModal from '../components/BatchManualOrdersModal';

const filterNotActive = (orders: OrderItem[], role: UserRole): OrderItem[] => orders.filter((o) => o.order_status === 'pending' && (o.is_manual || role === 'ROOT'));

const EventOrders: React.FC = () => {
  const navigate = useNavigateWithQuery();
  const { event, eventLoading } = useGetSelectedEvent();
  const { eventId, businessId } = useGetParams();
  const { token } = useSelector(
    (state: RootState) => state.auth,
  );
  const role = useGetUserRole();
  const userData = useSelector((state: RootState) => state.auth.user);
  const { data: eventOrders, isFetching } = useGetEventOrderItemsQuery(
    {
      businessId: event?.business_id || '',
      eventId,
      userId: role === 'SELLER' ? userData?.id : undefined,
    },
    { skip: !event || role === 'SELLER' },
  );

  const { data: showtimes } = useGetShowtimesQuery({ eventId: eventId || '', businessId: businessId || '' });
  const [pendingOrders, setPendingOrders] = useState<OrderItem[]>([]);
  const [activeOrders, setActiveOrders] = useState<OrderItem[]>([]);
  const [extraOrders, setExtraOrders] = useState<OrderItem[]>([]);
  const [isDownloading, setIsDownloading] = useState(false);
  const [tab, setTab] = useState<'confirmed' | 'pending' | 'pending-payments'>('confirmed');
  const [isSellerSalesEnabled, setIsSellerSalesEnabled] = useState(false);
  const [pendingPayments, setPendingPayments] = useState<OrderItem[]>([]);
  const [currentTab, setCurrentTab] = useState<string | undefined>(showtimes?.[0]?.id || undefined);
  const [showBatchManualModal, setShowBatchManualModal] = useState(false);

  useEffect(() => {
    if (event) {
      setIsSellerSalesEnabled(
        role === 'ADMIN' || role === 'ROOT' || event.seller_sales_enabled,
      );
    }
  }, [event]);

  const parseApprovedItems = (orders: OrderItem[]): OrderItem[] => {
    if (!event?.has_showtimes) {
      return orders
        .filter((o) => o.order_status === 'paid');
    }
    return orders
      .filter((o) => o.order_status === 'paid' && o.ticket_is_extra && o.showtime_id === currentTab);
  };

  const parseExtraItems = (orders: OrderItem[]): OrderItem[] => {
    if (!event?.has_showtimes) {
      return orders
        .filter((o) => o.order_status === 'paid');
    }
    return orders
      .filter((o) => o.order_status === 'paid' && o.ticket_is_extra && o.showtime_id === currentTab);
  };

  useEffect(() => {
    const orders = eventOrders;
    if (orders) {
      setPendingOrders(filterNotActive(orders, role));
      setActiveOrders(parseApprovedItems(orders));
      setExtraOrders(parseExtraItems(orders.filter((o) => o.ticket_is_extra)));
      setPendingPayments(orders.filter((o) => o.order_status === 'pending'));
    }
  }, [eventOrders, currentTab]);

  const handleDownloadExport = () => {
    setIsDownloading(true);
    axios
      .get(
        `${config.baseURL}/v1/businesses/${event?.business_id}/events/${eventId}/orders?xlsx=true`,
        {
          responseType: 'blob',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'lista.xlsx');
        document.body.appendChild(link);
        link.click();
      })
      .catch((e) => console.error(e))
      .finally(() => setIsDownloading(false));
  };

  const handleAddOrder = () => {
    if (event?.has_simplified_selling) {
      navigate('simplified-manual', { replace: true });
    } else {
      navigate('new/tickets', { replace: true });
    }
  };

  return (
    <div className="flex flex-col gap-8 w-full my-6">
      <AdminSectionTitle
        title="Eventos"
        subtitle={`${event?.name} - Asistentes`}
        mainButtons={isAllowed(userData, [UserRole.SELLER, UserRole.ADMIN, UserRole.COPRODUCER], event?.business_id ?? '') ? [
          {
            label: isSellerSalesEnabled
              ? 'Vender entradas'
              : 'Venta no habilitada',
            onClick: handleAddOrder,
            variant: 'secondary',
            disabled: !isSellerSalesEnabled,
            icon: <CoinsHand className='text-xl' />,
          },
          {
            label: 'Crear órdenes en lote',
            onClick: () => setShowBatchManualModal(true),
            variant: 'secondary',
            hidden: role !== 'ROOT',
          },
          {
            label: 'Impresion',
            onClick: () => navigate('physical-tickets'),
            variant: 'secondary',
            hidden: role !== 'ROOT',
          },
        ] : []}
      />
      <div className="glass-surface flex flex-col gap-4">
        {(role === 'ADMIN' || role === 'ROOT') && (
          <HorizontalTabs
            tabs={[
              {
                label: 'Entradas confirmadas',
                onClick: () => {
                  setTab('confirmed');
                },
                selected: tab === 'confirmed',
              },
              ...((event?.allow_manual_payments || role === 'ROOT') ? [{
                label: 'Solicitudes pendientes',
                onClick: () => {
                  setTab('pending');
                },
                selected: tab === 'pending',
              }] : []),
              {
                label: 'Pagos pendientes',
                onClick: () => {
                  setTab('pending-payments');
                },
                selected: tab === 'pending-payments',
                hidden: role !== 'ROOT',
              },
            ]}
          />
        )}

        {activeOrders && tab === 'confirmed' && event?.has_showtimes && showtimes && showtimes.length > 0 && (
          <ShowtimeTabs
            event={event}
            onTabChange={setCurrentTab}
            selectedTab={currentTab}
          />
        )}

        {pendingOrders && tab === 'pending' && (
          <div>
            {isFetching || eventLoading ? (
              <OrdersTableSkeleton />
            ) : (
              <Fragment>
                <PendingOrdersTable
                  orders={pendingOrders}
                  eventId={eventId}
                  businessId={businessId}
                />
              </Fragment>
            )}
          </div>
        )}

        {activeOrders && tab === 'confirmed' && (
          <div className="flex flex-col gap-4">
            {isFetching ? (
              <OrdersTableSkeleton />
            ) : (
              <ApprovedOrdersTable
                orderItems={activeOrders}
                buttonClick={handleDownloadExport}
                loading={isDownloading}
              />
            )}
          </div>
        )}

        {pendingPayments && tab === 'pending-payments' && (
          <div>
            <PendingPaymentsTable
              orders={pendingPayments}
            />
          </div>
        )}

      </div>

      {extraOrders && extraOrders.length > 0 && tab === 'confirmed' && (
        <div className="glass-surface">
          <div className="flex justify-between py-2">
            <h1 className="text-xl">Extras vendidos</h1>
          </div>
          {isFetching ? (
            <OrdersTableSkeleton />
          ) : (
            <ApprovedOrdersTable orderItems={extraOrders} />
          )}
        </div>
      )}

      {role === 'ROOT' && (
        <BatchManualOrdersModal
          isOpen={showBatchManualModal}
          onClose={() => setShowBatchManualModal(false)}
        />
      )}
    </div>
  );
};

export default EventOrders;
